"use client";

import Image from "next/image";
import Link from "next/link";
import { ChevronDown, Menu, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useUser } from "@/contexts/UserContext";
import { brandAsset, BRAND_LOGO } from "@/lib/brandAssets";
import { marketingModules, moduleGroups } from "@/lib/marketing-site";

const HeaderDark = () => {
  const { user } = useUser();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProductOpen, setIsProductOpen] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const organizationId = user?.organizations?.[0]?.id;
  const closeMenus = useCallback(() => {
    setIsMenuOpen(false);
    setIsProductOpen(false);
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeMenus();
    };
    const handlePointerDown = (event: MouseEvent) => {
      if (
        headerRef.current &&
        !headerRef.current.contains(event.target as Node)
      ) {
        closeMenus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("mousedown", handlePointerDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("mousedown", handlePointerDown);
    };
  }, [closeMenus]);

  return (
    <header
      ref={headerRef}
      className="fixed inset-x-0 top-0 z-50 px-3 pt-3 sm:px-5 sm:pt-4"
    >
      <nav className="relative mx-auto flex h-16 max-w-[1220px] items-center justify-between rounded-2xl border border-black/10 bg-white px-4 shadow-[0_16px_45px_-25px_rgba(20,24,30,0.38)] sm:px-5">
        <Link
          href="/"
          onClick={closeMenus}
          aria-label="AchieveApex, ir al inicio"
          className="flex shrink-0 items-center rounded-lg focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#3B82F6]/60"
        >
          <Image
            src={brandAsset(BRAND_LOGO)}
            alt="AchieveApex"
            width={148}
            height={40}
            priority
            className="h-auto w-[128px] object-contain sm:w-[142px]"
          />
        </Link>

        <div className="hidden items-center gap-1 lg:flex">
          <button
            type="button"
            onClick={() => setIsProductOpen((value) => !value)}
            className="flex items-center gap-1.5 rounded-xl px-3 py-2 text-sm font-medium text-[#242620] transition-colors hover:bg-black/[0.04] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#3B82F6]/60"
            aria-expanded={isProductOpen}
            aria-controls="desktop-product-menu"
            aria-haspopup="true"
          >
            Producto
            <ChevronDown
              className={`h-3.5 w-3.5 transition-transform ${isProductOpen ? "rotate-180" : ""}`}
              aria-hidden="true"
            />
          </button>
          <Link
            href="/#como-funciona"
            onClick={closeMenus}
            className="rounded-xl px-3 py-2 text-sm font-medium text-[#242620] hover:bg-black/[0.04] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#3B82F6]/60"
          >
            Cómo funciona
          </Link>
          <Link
            href="/#soluciones"
            onClick={closeMenus}
            className="rounded-xl px-3 py-2 text-sm font-medium text-[#242620] hover:bg-black/[0.04] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#3B82F6]/60"
          >
            Soluciones
          </Link>
          <Link
            href="/pricing"
            onClick={closeMenus}
            className="rounded-xl px-3 py-2 text-sm font-medium text-[#242620] hover:bg-black/[0.04] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#3B82F6]/60"
          >
            Precios
          </Link>
          <Link
            href="/implementers"
            onClick={closeMenus}
            className="rounded-xl px-3 py-2 text-sm font-medium text-[#242620] hover:bg-black/[0.04] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#3B82F6]/60"
          >
            Implementadores
          </Link>
          <Link
            href="/#recursos"
            onClick={closeMenus}
            className="rounded-xl px-3 py-2 text-sm font-medium text-[#242620] hover:bg-black/[0.04] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#3B82F6]/60"
          >
            Recursos
          </Link>
        </div>

        <div className="hidden items-center gap-2 lg:flex">
          {organizationId ? (
            <Link
              href={`/${organizationId}/conversations`}
              className="rounded-xl bg-[#171914] px-4 py-2.5 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#3B82F6]/60"
            >
              Ir a mi cuenta
            </Link>
          ) : (
            <>
              <Link
                href="/login"
                className="rounded-xl px-3 py-2 text-sm font-semibold text-[#242620] hover:bg-black/[0.04] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#3B82F6]/60"
              >
                Iniciar sesión
              </Link>
              <Link
                href="/signup"
                className="rounded-xl bg-[#3B82F6] px-4 py-2.5 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-black/15"
              >
                Empezar gratis
              </Link>
            </>
          )}
        </div>

        <button
          type="button"
          onClick={() => {
            setIsMenuOpen((value) => !value);
            setIsProductOpen(false);
          }}
          className="touch-manipulation rounded-xl border border-black/10 p-2.5 text-[#242620] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#3B82F6]/60 lg:hidden"
          aria-label={isMenuOpen ? "Cerrar menú" : "Abrir menú"}
          aria-expanded={isMenuOpen}
        >
          {isMenuOpen ? (
            <X className="h-5 w-5" />
          ) : (
            <Menu className="h-5 w-5" />
          )}
        </button>

        {isProductOpen && (
          <div
            id="desktop-product-menu"
            className="absolute left-1/2 top-[72px] hidden w-[calc(100vw-40px)] max-w-[1080px] -translate-x-1/2 rounded-[1.75rem] border border-black/10 bg-white p-4 shadow-[0_30px_80px_-30px_rgba(20,24,30,0.35)] lg:block"
          >
            <div className="grid grid-cols-4 gap-3">
              {moduleGroups.map((group) => (
                <div key={group.label} className="rounded-2xl bg-[#f5f6f2] p-4">
                  <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-black/40">
                    {group.label}
                  </p>
                  <div className="space-y-1">
                    {group.slugs.map((slug) => {
                      const product = marketingModules.find(
                        (item) => item.slug === slug,
                      );
                      if (!product) return null;
                      return (
                        <Link
                          key={product.slug}
                          href={`/producto/${product.slug}`}
                          onClick={closeMenus}
                          className="block rounded-xl px-3 py-2.5 transition-colors hover:bg-white"
                        >
                          <span className="block text-sm font-semibold text-[#171914]">
                            {product.name}
                          </span>
                          <span className="mt-0.5 block text-xs leading-5 text-black/45">
                            {product.outcome}
                          </span>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            <Link
              href="/producto"
              onClick={closeMenus}
              className="mt-3 flex items-center justify-between rounded-xl border border-black/10 px-4 py-3 text-sm font-semibold hover:bg-[#f8f9f6]"
            >
              Explorar toda la plataforma
              <span aria-hidden="true">→</span>
            </Link>
          </div>
        )}
      </nav>

      {isMenuOpen && (
        <div className="mx-auto mt-2 max-h-[calc(100dvh-100px)] max-w-[1220px] overflow-y-auto rounded-2xl border border-black/10 bg-white p-4 shadow-2xl lg:hidden">
          <button
            type="button"
            onClick={() => setIsProductOpen((value) => !value)}
            className="flex w-full items-center justify-between rounded-xl px-3 py-3 text-left text-sm font-semibold"
          >
            Producto
            <ChevronDown
              className={`h-4 w-4 transition-transform ${isProductOpen ? "rotate-180" : ""}`}
            />
          </button>
          {isProductOpen && (
            <div className="mb-3 grid gap-1 rounded-xl bg-[#f5f6f2] p-2 sm:grid-cols-2">
              {marketingModules.map((module) => (
                <Link
                  key={module.slug}
                  href={`/producto/${module.slug}`}
                  onClick={closeMenus}
                  className="rounded-lg px-3 py-2 text-sm text-black/65 hover:bg-white hover:text-black"
                >
                  {module.name}
                </Link>
              ))}
            </div>
          )}
          {[
            ["Cómo funciona", "/#como-funciona"],
            ["Soluciones", "/#soluciones"],
            ["Precios", "/pricing"],
            ["Implementadores", "/implementers"],
            ["Recursos", "/#recursos"],
          ].map(([label, href]) => (
            <Link
              key={href}
              href={href}
              onClick={closeMenus}
              className="block rounded-xl px-3 py-3 text-sm font-semibold"
            >
              {label}
            </Link>
          ))}
          <div className="mt-3 grid gap-2 border-t border-black/10 pt-4 sm:grid-cols-2">
            {organizationId ? (
              <Link
                href={`/${organizationId}/conversations`}
                onClick={closeMenus}
                className="rounded-xl bg-[#171914] px-4 py-3 text-center text-sm font-semibold text-white sm:col-span-2"
              >
                Ir a mi cuenta
              </Link>
            ) : (
              <>
                <Link
                  href="/login"
                  onClick={closeMenus}
                  className="touch-manipulation rounded-xl border border-black/10 px-4 py-3 text-center text-sm font-semibold"
                >
                  Iniciar sesión
                </Link>
                <Link
                  href="/signup"
                  onClick={closeMenus}
                  className="touch-manipulation rounded-xl bg-[#3B82F6] px-4 py-3 text-center text-sm font-semibold text-white"
                >
                  Empezar gratis
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default HeaderDark;
